import { useApp } from '../context/AppContext';

const qualitative = (pct) => (pct >= 80 ? 'Excellent' : pct >= 60 ? 'Good' : pct >= 40 ? 'Developing' : 'Needs focus');

// Maps a blended mastery/accuracy percentage to a grade label appropriate to the
// qualification. Qualifications with real scaled scoring (SAT/ACT) don't get a fake
// precise number — they get an honest qualitative band instead.
export function gradeForQualification(qualification, pct) {
  const q = (qualification || '').toUpperCase();
  if (q === 'AP') {
    if (pct >= 80) return '5'; if (pct >= 65) return '4'; if (pct >= 50) return '3'; if (pct >= 35) return '2'; return '1';
  }
  if (q === 'IB') {
    if (pct >= 85) return '7'; if (pct >= 75) return '6'; if (pct >= 65) return '5'; if (pct >= 50) return '4';
    if (pct >= 35) return '3'; if (pct >= 20) return '2'; return '1';
  }
  if (q === 'A-LEVEL') {
    if (pct >= 85) return 'A*'; if (pct >= 75) return 'A'; if (pct >= 65) return 'B'; if (pct >= 55) return 'C'; if (pct >= 45) return 'D'; return 'E';
  }
  if (q === 'GCSE' || q === 'IGCSE') {
    if (pct >= 85) return '9'; if (pct >= 75) return '8'; if (pct >= 65) return '7'; if (pct >= 58) return '6';
    if (pct >= 50) return '5'; if (pct >= 42) return '4'; if (pct >= 34) return '3'; if (pct >= 25) return '2'; return '1';
  }
  // SAT/ACT/Custom/other — no honest scaled-score conversion without real norm data.
  if (pct >= 80) return 'Excellent'; if (pct >= 60) return 'Good'; if (pct >= 40) return 'Developing'; return 'Needs focus';
}

// Blends subtopic mastery with recent question-bank/quiz/diagnostic accuracy (more
// recent attempts weighted more) so the prediction reflects current performance, not
// just stale confidence ratings. Falls back to pure mastery if there's no score history.
export default function usePredictedGrade(subjectId) {
  const { getSubjectProgress, scores, getSubjectById, subjects } = useApp();

  // No subjectId = overall across every subject. Qualifications differ (AP vs IB vs
  // A-Level letter/number scales aren't comparable), so "overall" is a blended
  // percentage with a qualitative label rather than a single fabricated grade letter.
  if (!subjectId) {
    const withData = subjects.map(s => getSubjectProgress(s.id)).filter(p => p.total > 0);
    if (!withData.length) return { grade: null, pct: 0, basis: '', hasData: false, lowBasis: true };
    const avgPct = Math.round(withData.reduce((sum, p) => sum + p.pct, 0) / withData.length);
    const totalRatedTopics = withData.reduce((sum, p) => sum + p.total, 0);
    return {
      grade: qualitative(avgPct),
      pct: avgPct,
      basis: `Average subtopic mastery across your ${withData.length} subject${withData.length === 1 ? '' : 's'} (${totalRatedTopics} rated topic${totalRatedTopics === 1 ? '' : 's'}).`,
      hasData: true,
      lowBasis: totalRatedTopics < 8,
    };
  }

  const subject = getSubjectById(subjectId);
  const prog = getSubjectProgress(subjectId);

  const subjectScores = scores
    .filter(s => s.subjectId === subjectId && typeof s.percentage === 'number')
    .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))
    .slice(0, 6);

  let recentAccuracy = null;
  if (subjectScores.length) {
    const weights = subjectScores.map((_, i) => subjectScores.length - i);
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    recentAccuracy = subjectScores.reduce((sum, s, i) => sum + s.percentage * weights[i], 0) / totalWeight;
  }

  const blended = recentAccuracy !== null ? Math.round(prog.pct * 0.55 + recentAccuracy * 0.45) : prog.pct;
  const grade = prog.total > 0 ? gradeForQualification(subject?.qualification, blended) : null;

  const basis = recentAccuracy !== null
    ? `Based on ${prog.pct}% subtopic mastery (${prog.green} confident, ${prog.amber} in progress, ${prog.red} gaps) and ${Math.round(recentAccuracy)}% accuracy across your last ${subjectScores.length} practice session${subjectScores.length === 1 ? '' : 's'}.`
    : `Based on ${prog.pct}% subtopic mastery (${prog.green} confident, ${prog.amber} in progress, ${prog.red} gaps). Practice more to factor in real accuracy.`;

  // Under ~5 rated topics and no score history, the prediction rests on too
  // little data to be reliable — surfaced as a visible warning in the UI.
  return { grade, pct: blended, basis, hasData: prog.total > 0, lowBasis: prog.total < 5 && !subjectScores.length };
}
